import { useMemo, useState } from "react";
import moment from "moment-timezone";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import {
  useLazyGetDevicesPerformanceBetweenDatesReportQuery,
  useGetDevicePerformancePdfReportMutation,
} from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import useReportFirms from "../../../Hooks/useReportFirms";
import { firmHasActiveProduct } from "../../../utils/consumptionProductGroup";
import {
  FirmDropdown,
  TextInput,
  NumberInput,
  CustomDropdown,
} from "../../../Components/atoms";
import { getMonthStart } from "../../../utils/reportDateDefaults";

/**
 * CİHAZ PERFORMANS RAPORU — Excel/tablo yerine PDF belgesi üretir.
 *
 * Desen `FirmPerformanceUnifiedReportPage` ile aynıdır: `ReportLayout`
 * `showExcel={false}` + başlıkta "PDF İndir" + solda `ReportFilterCard`
 * + sağda `ReportPdfPreview`.
 *
 * AKIŞ: `devicesPerformanceBetweenDates` uç noktasından ham satırlar çekilir
 * (veri kaynağı DEĞİŞMEDİ), "Ortalama Tüketim Filtresi" burada uygulanır ve
 * süzülmüş satırlar PDF uç noktasına gönderilir. Eşik süzmesi eskiden de
 * istemcideydi; belge ile ekranda görülen listenin birebir aynı kalması için
 * süzme yeri korundu ve sunucu yalnızca belgeyi basıyor.
 *
 * SIRALAMA: sunucunun döndürdüğü seri no sırası korunur (eski sayfadaki
 * `sortField` durumu hiçbir arayüz öğesine bağlı değildi, yani ölü koddu).
 */
export default function ReportDevicesPerformancePage() {
  const { auth } = useAuth();
  const token = auth.accessToken;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

  const [serialNo, setSerialNo] = useState("");
  const [firmId, setFirmId] = useState("");
  const [firmName, setFirmName] = useState("");
  const [productName, setProductName] = useState("");
  const [averageFilter, setAverageFilter] = useState(0);

  // Ürün tipleri seçenekleri
  const productTypeOptions = [
    { id: "all", label: "Tümü", value: "" },
    { id: "cay", label: "Çay", value: "Çay" },
    { id: "turk-kahvesi", label: "Türk Kahvesi", value: "Türk Kahvesi" },
    { id: "filtre-kahve", label: "Filtre Kahve", value: "Filtre Kahve" },
  ];

  const getStartOfDay = (date) => moment(date).tz(tz).startOf("day").toDate();
  const getEndOfDay = (date) => moment(date).tz(tz).endOf("day").toDate();

  // Başlangıç varsayılanı ayın ilk günü (Workspace #154) — "Bu ay" hazır seçeneği
  const [startDate, setStartDate] = useState(() => getMonthStart(tz));
  const [endDate, setEndDate] = useState(getEndOfDay(new Date()));
  const [activePreset, setActivePreset] = useState("thismonth");

  // Rapor çalıştırıldıktan sonraki durum. `deviceCount === 0` ("kriterlere
  // uyan cihaz yok") ile `null` ("henüz çalıştırılmadı") ayrı durumlardır.
  const [deviceCount, setDeviceCount] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const { firms, isLoading: firmDataIsLoading } = useReportFirms(token);

  // ÜRÜNE GÖRE FİRMA LİSTESİ (2026-09-14): seçili üründe ürün ayarı KAPALI
  // olan ya da o ürünü hiç tanımlamamış firmalar listelenmez. Kural performans
  // raporlarında vardı ama bu sayfaya hiç uygulanmamıştı; Espresso'su kapalı
  // bir firma seçilebiliyor ve rapor boş çıkıyordu.
  const selectableFirms = useMemo(
    () => (firms || []).filter((f) => firmHasActiveProduct(f, productName)),
    [firms, productName],
  );

  const [getDevicesPerformanceReport] =
    useLazyGetDevicesPerformanceBetweenDatesReportQuery();
  const [generatePdf, pdfState] = useGetDevicePerformancePdfReportMutation();

  const report = pdfState.data?.data;
  const pdfBase64 = report?.pdfBase64;

  const handleFirmChange = (firm) => {
    if (!firm || firm._id === "all") {
      setFirmId("");
      setFirmName({ _id: "all", firmName: "Tüm Firmalar" });
    } else {
      setFirmId(firm._id);
      setFirmName(firm);
    }
  };

  /**
   * Raporu çalıştırır. Hızlı aralık butonları da buraya düşer — tarihler
   * state'e yazılmadan önce parametre olarak geçilebilsin diye aralık
   * argüman olarak alınır (setState asenkron; preset'te eski tarih gönderme
   * hatası bu yüzden oluşuyordu).
   */
  const runReport = async ({ start, end } = {}) => {
    const periodStart = start || startDate;
    const periodEnd = end || endDate;
    if (!productName || !periodStart || !periodEnd) return;

    setRunError(null);
    setIsRunning(true);
    try {
      const response = await getDevicesPerformanceReport({
        token,
        serialNo: serialNo || "undefined",
        firmId: firmId || "undefined",
        productName,
        startDate: periodStart.toISOString(),
        endDate: periodEnd.toISOString(),
      }).unwrap();

      // Eşik süzmesi istemcide kalır (eski davranışın aynısı).
      const filtered = (response?.data || []).filter(
        (item) => averageFilter === 0 || item.dailyAverage < averageFilter
      );
      setDeviceCount(filtered.length);

      // Boş sonuçta belge basılmaz: sunucuda gereksiz bir puppeteer turu ve
      // kullanıcı için anlamsız bir çıktı olurdu.
      if (filtered.length === 0) {
        pdfState.reset?.();
        return;
      }

      await generatePdf({
        token,
        rows: filtered,
        productName,
        firmName: firmId ? firmName?.firmName || "" : "Tüm Firmalar",
        serialNo,
        averageFilter,
        startDate: periodStart.toISOString(),
        endDate: periodEnd.toISOString(),
      }).unwrap();
    } catch (error) {
      setRunError(error);
    } finally {
      setIsRunning(false);
    }
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);
    setActivePreset(null);
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
    setActivePreset(null);
  };

  const applyPreset = (preset) => {
    const now = new Date();
    let newStart;
    let newEnd;

    if (preset === "today") {
      newStart = getStartOfDay(now);
      newEnd = getEndOfDay(now);
    } else if (preset === "yesterday") {
      const yest = moment(now).tz(tz).subtract(1, "day");
      newStart = yest.clone().startOf("day").toDate();
      newEnd = yest.clone().endOf("day").toDate();
    } else if (preset === "thisweek") {
      newStart = moment(now).tz(tz).startOf("isoWeek").toDate();
      newEnd = getEndOfDay(now);
    } else if (preset === "thismonth") {
      newStart = moment(now).tz(tz).startOf("month").toDate();
      newEnd = getEndOfDay(now);
    } else {
      return;
    }

    setStartDate(newStart);
    setEndDate(newEnd);
    setActivePreset(preset);

    // Ürün seçiliyse hızlı aralık doğrudan raporu üretir (eski davranış).
    if (productName) runReport({ start: newStart, end: newEnd });
  };

  const handleClearFilters = () => {
    setSerialNo("");
    setFirmId("");
    setFirmName("");
    setProductName("");
    setAverageFilter(0);
    setStartDate(getMonthStart(tz));
    setEndDate(getEndOfDay(new Date()));
    setActivePreset("thismonth");
    setDeviceCount(null);
    setRunError(null);
    pdfState.reset?.();
  };

  const errorMessage =
    runError?.data?.message ||
    runError?.error ||
    runError?.message ||
    "Rapor oluşturulurken bir hata oluştu.";

  const activeFilterChips = [
    { label: "Ürün", value: productName },
    { label: "Seri No", value: serialNo },
    { label: "Firma", value: firmId ? firmName?.firmName : "" },
    {
      label: "Ortalama Eşiği",
      value: averageFilter > 0 ? `< ${averageFilter}` : "",
    },
  ];

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, report?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-SemiBold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  return (
    <ReportLayout
      title="Cihaz Performans Raporu"
      showExcel={false}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
          title="Cihaz Performans Filtreleme"
          description="Rapor için filtre seçeneklerini belirleyin"
          showDatePickers={true}
          startDate={startDate}
          setStartDate={handleStartDateChange}
          endDate={endDate}
          setEndDate={handleEndDateChange}
          onSearch={() => runReport()}
          onClear={handleClearFilters}
          isLoading={isRunning}
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          searchDisabled={!productName || !startDate || !endDate}
          searchDisabledMessage="Ürün adı ve tarih aralığı seçiniz"
          activeFilters={activeFilterChips}
        >
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs font-SemiBold text-gray-500 mr-1">
              Hızlı Aralık:
            </span>
            {[
              { key: "today", label: "Bugün" },
              { key: "yesterday", label: "Dün" },
              { key: "thisweek", label: "Haftalık" },
              { key: "thismonth", label: "Aylık" },
            ].map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => applyPreset(p.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-SemiBold transition-colors ${
                  activePreset === p.key
                    ? "bg-main text-white border border-main"
                    : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Seri No */}
            <TextInput
              label="Seri No"
              placeholder="Seri numarası girin..."
              value={serialNo}
              onChange={(e) => setSerialNo(e.target.value)}
            />

            {/* Ürün Adı */}
            <CustomDropdown
              value={productTypeOptions.find(
                (opt) => opt.value === productName
              )}
              onChange={(option) => {
                // Ürün değişince firma listesi daralıyor; listede kalmayabilecek
                // seçim sıfırlanır ki sessizce boş rapor üretilmesin.
                setProductName(option?.value || "");
                handleFirmChange(null);
              }}
              options={productTypeOptions}
              placeholder="Ürün seçin..."
              showSearch={false}
            />

            {/* Firma */}
            <FirmDropdown
              value={firmName}
              onChange={handleFirmChange}
              firms={selectableFirms}
              loading={firmDataIsLoading}
            />

            {/* Ortalama Tüketim Filtresi */}
            <NumberInput
              label="Ortalama Tüketim Filtresi"
              placeholder="Filtre değeri..."
              value={averageFilter === 0 ? "" : averageFilter}
              onChange={(value) => {
                setAverageFilter(value === "" ? 0 : Number(value));
              }}
              min={0}
            />
          </div>
        </ReportFilterCard>
      }
    >
      {isRunning && (
        <ReportLoadingState
          title="Cihaz Performans Raporu Hazırlanıyor"
          description="Veriler toplanıyor ve belge oluşturuluyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={6}
          skeletonColumns={4}
        />
      )}

      {!isRunning && runError && (
        <ReportErrorState
          title="Rapor Yüklenirken Hata Oluştu"
          message={errorMessage}
          icon="warning"
        />
      )}

      {!isRunning && !runError && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`Cihaz Performans Raporu — ${report?.periodLabel || ""}`}
        />
      )}

      {!isRunning && !runError && !pdfBase64 && (
        <ReportEmptyState
          title={deviceCount === 0 ? "Veri Bulunamadı" : "Rapor Bekleniyor"}
          message={
            deviceCount === 0
              ? "Seçilen kriterlere uygun cihaz performans verisi bulunamadı."
              : "Ürün ve tarih aralığını seçip 'Raporu Getir' butonuna basın."
          }
          icon="chart"
        />
      )}
    </ReportLayout>
  );
}
